import { mix } from 'polished';
import styled, { css } from 'styled-components';

import { convertHexToRgb, rem } from '../../utils';
import { ColorIconProps } from '../ColorBullet';

export type BulletStyle = 'circle' | 'rect' | 'rounded';
export type BulletSize = 'Small' | 'Medium' | 'Large';

export const StyledWrapper = styled.div<{ type?: BulletStyle }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible;
`;

export const StyledTransparent = css`
  background: ${({ theme }) => theme.Color.NeutralsFixed.Light};
  border: 1px solid ${({ theme }) => theme.Color.Neutrals.Border};

  &::after {
    content: '';
    position: absolute;
    inset-inline-end: 1px;
    top: 0;
    display: block;
    border-radius: 0;
    width: calc(100% + 10px);
    height: 2px;
    transform-origin: right;
    transform: rotate(-45deg);
    background-color: #f5222d;
  }
`;

export const StyledPicker = css`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  z-index: 1;
  background: conic-gradient(
    rgba(255, 0, 0, 1) 0%,
    rgba(255, 154, 0, 1) 10%,
    rgba(208, 222, 33, 1) 20%,
    rgba(79, 220, 74, 1) 30%,
    rgba(63, 218, 216, 1) 40%,
    rgba(47, 201, 226, 1) 50%,
    rgba(28, 127, 238, 1) 60%,
    rgba(95, 21, 242, 1) 70%,
    rgba(186, 12, 248, 1) 80%,
    rgba(251, 7, 217, 1) 90%,
    rgba(255, 0, 0, 1) 100%
  );

  &::after {
    z-index: 2;
    content: '';
    position: absolute;
    display: inline-block;
    background-color: ${({ theme }) => theme.Color.Neutrals.Paper};
    width: 70%;
    height: 70%;
  }

  svg {
    z-index: 3;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    margin: auto;
    text-align: center;
  }
`;

export const StyledSelected = css`
  box-shadow: inset 0 0 0
    ${({ theme }) => `${rem(theme.Border.Size.Medium)} ${theme.Color.Neutrals.Paper}`};
  border: ${rem(1.5)} solid ${({ theme }) => theme.Color.Brand.Primary};
`;

export const StyledHover = css`
  box-shadow: inset 0 0 0
    ${({ theme }) => `${rem(theme.Border.Size.Medium)} ${theme.Color.Neutrals.Paper}`};
  border: ${rem(1.5)} solid ${({ theme }) => theme.Color.Neutrals.Border};
`;

export const variantCircle = css`
  border-radius: 50%;

  &::after {
    border-radius: 50%;
  }
`;

export const variantRect = css`
  border-radius: 0;
  border: none;

  &::after {
    border-radius: 0;
  }
`;

export const variantRounded = css`
  ${({ theme }) => `
    border-radius: ${rem(theme.Border.Radius.Small)};

    &::after {
      border-radius: ${rem(theme.Border.Radius.Small)};
    }
  `}
`;

export const StyledColorButton = styled.div<{
  $color: ColorIconProps['color'];
  $isPicker?: boolean;
  $isReadOnly?: boolean;
  $isSelected?: boolean;
  $size?: BulletSize;
  $variant?: BulletStyle;
}>`
  display: block;
  position: relative;
  padding: 0;
  box-sizing: border-box;

  ${({ $color, theme, $isPicker }) => {
    if ($color === 'mixed') {
      return css`
        background: conic-gradient(
          hsl(0, 100%, 75%),
          /* Lighter red */ hsl(30, 100%, 75%),
          /* Lighter orange */ hsl(60, 100%, 75%),
          /* Lighter yellow */ hsl(120, 100%, 75%),
          /* Lighter green */ hsl(180, 100%, 75%),
          /* Lighter cyan */ hsl(240, 100%, 75%),
          /* Lighter blue */ hsl(300, 100%, 75%),
          /* Lighter magenta */ hsl(360, 100%, 75%) /* Lighter red */
        );
        border: none;
      `;
    } else {
      return css`
        background: ${$color || '#000000'};
        border-style: solid;
        border-width: ${$isPicker ? 0 : rem(theme.Border.Size.Small)};
        border-color: ${() => {
          if ($color?.startsWith('#')) {
            return mix(0.88, $color.substring(0, 7) || '#000000', theme.Color.Neutrals.Dark);
          } else if ($color === 'transparent') {
            return theme.Color.Neutrals.Border;
          }

          return $color;
        }};
      `;
    }
  }}

  cursor: pointer;
  overflow: hidden;
  transition: ${({ theme }) =>
    `border-color ${theme.Transition.Timing.Out}, box-shadow ${theme.Transition.Timing.Out};`};

  &:hover {
    ${({ $variant, $isPicker, $color }) =>
      $variant !== 'rect' && !$isPicker && $color !== 'transparent' && StyledHover}

    transition: ${({ theme }) =>
      `border-color ${theme.Transition.Timing.In}, box-shadow ${theme.Transition.Timing.In};`};
  }

  ${({ $isPicker, $color }) => !$isPicker && $color === 'transparent' && StyledTransparent}

  ${({ $variant }) => {
    switch ($variant) {
      case 'circle':
        return variantCircle;
      case 'rect':
        return variantRect;
      case 'rounded':
        return variantRounded;
      default:
        return '';
    }
  }}

  ${({ $isPicker }) => $isPicker && StyledPicker}
  ${({ $isSelected }) => $isSelected && StyledSelected}

  ${({ $size, theme }) => css`
    width: ${rem(theme.Icon.Size[$size])};
    height: ${rem(theme.Icon.Size[$size])};
  `}
`;

export const StyledInputColor = styled.input<{
  $isReadOnly: boolean;
}>`
  opacity: 0;
  appearance: none;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  cursor: pointer;
  z-index: ${({ theme }) => theme.ZIndex.ZIndex.Elevated};

  ${({ $isReadOnly }) =>
    $isReadOnly &&
    css`
      pointer-events: none;
    `}
`;

export const StyledIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 100;
  border-top-right-radius: ${({ theme }) => rem(theme.Border.Radius.XSmall)};
  border-bottom-left-radius: ${({ theme }) => rem(theme.Border.Radius.Small)};
  background-color: ${({ theme }) => {
    const colorInRgb = convertHexToRgb(theme.Color.NeutralsFixed.Dark);

    if (colorInRgb) {
      const [red, green, blue] = colorInRgb;

      return `rgba(${red}, ${green}, ${blue}, 0.65)`;
    }

    return theme.Color.NeutralsFixed.Dark;
  }};
  cursor: pointer;
  width: 0.75rem;
  height: 0.75rem;
  transition: ${({ theme }) => `opacity ${theme.Transition.Timing.Out}`};

  &:hover {
    opacity: 0.8;
    transition: ${({ theme }) => `opacity ${theme.Transition.Timing.In}`};
  }

  i {
    width: 0.5rem !important;
    height: 0.5rem !important;
  }

  svg {
    display: flex !important;
    width: 0.5rem !important;
    height: 0.5rem !important;
  }
`;

export const StyledPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => rem(theme.Spacing.Size.Small)};
`;
